<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import {
  createFunilLabel,
  funilLabelTitle,
} from 'dashboard/api/aceleres/funilLabels';
import NextButton from 'dashboard/components-next/button/Button.vue';

/**
 * Aceleres: criar uma etiqueta visual com nome e cor próprios.
 *
 * Existe porque o menu de botao direito da conversa nao comporta um campo de
 * texto: ele fecha ao primeiro clique fora e e posicionado por coordenada.
 */
const props = defineProps({
  accountLabels: {
    type: Array,
    default: () => [],
  },
  /** Nome ja digitado no seletor, para nao se ter de escrever duas vezes. */
  prefill: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['created', 'close']);

const { t } = useI18n();
const store = useStore();

const name = ref(props.prefill || '');
const color = ref('#64748B');
const isSaving = ref(false);
const errorMessage = ref('');
const input = ref(null);

const parsedTitle = computed(() => funilLabelTitle(name.value));

const titleTaken = computed(() =>
  props.accountLabels.some(label => label.title === parsedTitle.value)
);

const canSave = computed(
  () => !isSaving.value && parsedTitle.value.length >= 2 && !titleTaken.value
);

onMounted(() => nextTick(() => input.value?.focus()));

const save = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  errorMessage.value = '';

  try {
    const { payload } = await createFunilLabel(name.value, color.value);
    // A lista da conta precisa conhecer a etiqueta antes de ela ser atribuida:
    // e por ela que o cartao e a ficha decidem o que desenhar.
    await store.dispatch('labels/get');
    emit('created', payload);
  } catch (error) {
    errorMessage.value =
      error.message || t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_ERROR');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-auto p-8 overflow-auto">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL') }}
    </h2>
    <form class="flex flex-col w-full" @submit.prevent="save">
      <label class="w-full">
        <span class="text-sm text-n-slate-12">
          {{ t('CONTACT_PANEL.LABELS.LABEL_SELECT.NAME_FIELD') }}
        </span>
        <input
          ref="input"
          v-model="name"
          type="text"
          class="w-full mt-1"
          @input="errorMessage = ''"
        />
      </label>

      <label class="flex items-center gap-3 mt-4">
        <input v-model="color" type="color" class="w-10 h-10 p-0 border-0 cursor-pointer" />
        <span class="text-sm text-n-slate-12">Cor da etiqueta</span>
      </label>

      <span v-if="errorMessage" class="mt-1 text-xs text-n-ruby-11">
        {{ errorMessage }}
      </span>
      <span v-else-if="titleTaken" class="mt-1 text-xs text-n-slate-11">
        {{ t('CONTACT_PANEL.LABELS.LABEL_SELECT.ALREADY_EXISTS') }}
      </span>
      <span v-else-if="parsedTitle" class="mt-1 text-xs text-n-slate-11">
        {{ t('CONTACT_PANEL.LABELS.LABEL_SELECT.WILL_BE_NAMED') }}
        <b>{{ parsedTitle }}</b>
      </span>

      <div class="flex items-center justify-end w-full gap-2 mt-6">
        <NextButton
          faded
          slate
          type="button"
          :label="t('CONTACT_PANEL.LABELS.LABEL_SELECT.CANCEL')"
          @click="emit('close')"
        />
        <NextButton
          type="submit"
          :label="t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL')"
          :is-loading="isSaving"
          :disabled="!canSave"
        />
      </div>
    </form>
  </div>
</template>
